import app from 'app';
import _ from 'lodash';
import { parsePhone } from 'helpers/stringParsers';

import { logEvent, eventCategories } from 'helpers/analytics';
import { COLLECTIVE_TITLE } from '../../../collectiveTitle';

import userAccountTemplateUrl from './userAccount.html';
import userAccountEditTemplateUrl from './userAccountEdit.html';

// Modals
import mxConfirmationModal from 'components/modals/mxConfirmationModal/mxConfirmationModal';

// Templates
import editEmailTemplateUrl from './editEmail.html';
import editPasswordTemplateUrl from './editPassword.html';
import editPhoneNumberTemplateUrl from './editPhoneNumber.html';
import viewEmailTemplateUrl from './viewEmail.html';
import viewPasswordTemplateUrl from './viewPassword.html';
import viewPhoneNumberTemplateUrl from './viewPhoneNumber.html';
import PATTERNS from 'constants/patterns';

function UserAccountCtrl(
  $log,
  $q,
  $state,
  animatedScrollTop,
  Api,
  chModal,
  PhiAuthorization
) {
  const User = this;
  User.COLLECTIVE_TITLE = COLLECTIVE_TITLE;

  // Email update functions
  const changeEmail = email =>
    Api.post(`/base_user/${User.user.id}/change_email`, {
      oldEmail: User.user.email,
      email: email.toLowerCase(),
      password: User.currentPassword,
      path: '/verifyChangeEmail'
    });

  function showVerifyEmailConfirmation() {
    const deferred = $q.defer();

    chModal
      .open({
        component: mxConfirmationModal,
        locals: {
          body: `You should be receiving an email to confirm your updated email address.
          Please confirm so we can ensure that we are sending updates to the correct email address.`,
          cancel: 'I didn’t get the email',
          confirm: 'Got it, Thanks',
          header: 'Check your inbox for an email verification.'
        }
      })
      .on('close', emailReceived => {
        deferred.resolve(emailReceived);
      });

    return deferred.promise;
  }

  function resendEmail() {
    return changeEmail(User.user.pendingEmail).then(() => {
      chModal.open({
        component: mxConfirmationModal,
        locals: {
          header: 'Almost there...',
          body: `You should receive an email from us shortly with a link to verify your email.
            If you don't see it in the next few minutes (and it's not in your spam folder) send us a
            message`,
          confirm: 'close'
        }
      });
    });
  }

  function phiAuthorizationsMapper(authorization) {
    return {
      firstName: authorization.grantor.firstName,
      lastName: authorization.grantor.lastName,
      phiLevel: authorization.phiLevel,
      relationship: authorization.relationship
    };
  }

  function showErrorToast(field, errNum) {
    User.showToast = true;
    User.toastLevel = 'error';
    if (errNum === 912) {
      User.toastText = 'This email is already in use. Please try another.';
    } else {
      User.toastText = `Hmm, we ran into a snag saving your ${field}. Please try again`;
    }

    animatedScrollTop();
  }

  function showSuccessToast(field) {
    User.showToast = true;
    User.toastLevel = 'success';

    if (field === 'email') {
      User.toastText = 'Great! Check your inbox to verify this email address.';
    } else {
      User.toastText = `Your ${field} was successfully updated!`;
    }

    animatedScrollTop();
  }

  User.trackEditedPref = prefName => {
    logEvent(`${prefName}Edited`, {
      category: eventCategories.SETTINGS
    });
  };

  User.$onInit = () => {
    User.editEmailTemplateUrl = editEmailTemplateUrl;
    User.editPasswordTemplateUrl = editPasswordTemplateUrl;
    User.editPhoneNumberTemplateUrl = editPhoneNumberTemplateUrl;
    User.viewEmailTemplateUrl = viewEmailTemplateUrl;
    User.viewPasswordTemplateUrl = viewPasswordTemplateUrl;
    User.viewPhoneNumberTemplateUrl = viewPhoneNumberTemplateUrl;
    User.patterns = PATTERNS;

    // user this to avoid template errors but we really want the API response version as user data gets edited
    User.user = User.currentUser.user;

    // User Account Edit View helper function
    User.isEdit = {
      phone: false,
      email: false,
      password: false
    };

    User.displayStreetAddress = User.currentUser.preferredAddress;

    if (User.isSubscriber) {
      return PhiAuthorization.getAuthorizations({
        granteeId: User.user.id
      })
        .then(_.partialRight(_.get, 'phiAuthorizations'))
        .then(_.partialRight(_.map, phiAuthorizationsMapper))
        .then(_.partialRight(_.sortBy, 'firstName'))
        .then(_.partial(_.set, User, 'familyPermissions'));
    }
  };

  // User Account Read View helper functions
  User.getCityStateZip = address => {
    return address.city + ', ' + address.stateCode + ' ' + address.postalCode;
  };

  User.getPhoneNumber = () => {
    let result = '--';

    if (User.user.primaryPhone) {
      result = parsePhone(User.user.primaryPhone);
    }
    return result;
  };

  User.getEditText = item => {
    return User.isEdit[item] ? 'hide' : 'edit';
  };

  User.toggleEdit = item => {
    User.isEdit[item] = !User.isEdit[item];

    switch (item) {
      case 'edit-password':
        User.oldPassword = '';
        User.newPassword = '';
        User.newPasswordConfirmation = '';
        break;
      case 'edit-email':
        User.currentPassword = '';
        User.newEmail = '';
        User.newEmailConfirmation = '';
        break;
      case 'edit-phone':
        User.editablePrimaryPhone = '';
        break;
      default:
        break;
    }
  };

  // Phone number update functions
  User.savePhoneNumber = () => {
    if (User.editablePrimaryPhone === User.user.primaryPhone) {
      return User.toggleEdit('edit-phone');
    } else if (!User.phoneForm.$valid) {
      return;
    }

    return Api.put(`/person/${User.user.id}`, {
      primaryPhone: User.editablePrimaryPhone
    })
      .then(response => {
        User.user = response;
        User.toggleEdit('edit-phone');
        showSuccessToast('phone number');
      })
      .catch(() => {
        showErrorToast('phone number');
      });
  };

  User.saveEmail = () => {
    if (!User.emailForm.$valid) {
      return;
    }

    return changeEmail(User.newEmail)
      .then(() => {
        return showVerifyEmailConfirmation();
      })
      .then(emailReceived => {
        User.user.pendingEmail = User.newEmail;
        if (!emailReceived) {
          resendEmail();
        }

        User.toggleEdit('edit-email');
        showSuccessToast('email');
      })
      .catch(err => {
        User.emailForm.currentPassword.$error.badPass = true;
        showErrorToast('email', err.data.errorNumber);
      });
  };

  // Password reset functions
  User.savePassword = () => {
    if (!User.passwordForm.$valid) {
      return;
    }

    return Api.put(`/person/${User.user.id}/password`, {
      email: User.user.email,
      oldPassword: User.oldPassword,
      password: User.newPassword,
      passwordConfirm: User.newPasswordConfirmation
    })
      .then(() => {
        Reflect.deleteProperty(User, 'oldPassword');
        Reflect.deleteProperty(User, 'newPassword');
        Reflect.deleteProperty(User, 'newPasswordConfirmation');

        User.toggleEdit('edit-password');
        showSuccessToast('password');
      })
      .catch(() => {
        User.passwordForm.oldPassword.$error.badPass = true;
        showErrorToast('password');
      });
  };
}

// User Account (View)
app.component('userAccount', {
  bindings: {
    currentUser: '<',
    isSubscriber: '<'
  },
  controller: UserAccountCtrl,
  controllerAs: 'User',
  templateUrl: userAccountTemplateUrl
});

// User Account (Edit)
app.component('userAccountEdit', {
  bindings: {
    currentUser: '<',
    phiAuthLevels: '<',
    isSubscriber: '<'
  },
  controller: UserAccountCtrl,
  controllerAs: 'User',
  templateUrl: userAccountEditTemplateUrl
});

export const userAccount = 'userAccount';
export const userAccountEdit = 'userAccountEdit';
